import React, { useState } from 'react';
import { api } from '../../lib/api';

interface BackupSectionProps {
  onImportComplete: () => Promise<void>;
}

export const BackupSection: React.FC<BackupSectionProps> = ({ onImportComplete }) => {
  const [importType, setImportType] = useState<'csv_phrases' | 'markdown_nicknames'>('csv_phrases');
  const [rawContent, setRawContent] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState<string | null>(null);

  const handleOptimize = async () => {
    setIsOptimizing(true);
    setOptimizeResult(null);
    setErrorMsg(null);
    try {
      const res = await api.optimizeDatabase();
      setOptimizeResult(res.message);
      setTimeout(() => setOptimizeResult(null), 5000);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Database optimization failed');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result as string;
      setRawContent(text || '');
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rawContent.trim()) {
      setErrorMsg('Please paste content or select a file to import');
      return;
    }

    setIsImporting(true);
    setErrorMsg(null);
    setImportResult(null);

    try {
      const res = await api.importAdminContent({
        type: importType,
        content: rawContent,
      });

      setImportResult(
        `Successfully imported ${res.importedCount} new items (${res.skippedCount} existing items skipped).`
      );
      setRawContent('');
      await onImportComplete();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="admin-section backup-section">
      <div className="section-header">
        <h2>Backup, Export & Content Import</h2>
        <p className="section-description">
          Export keepsake content for safekeeping, or bulk-import updated multilingual love phrases and nicknames.
        </p>
      </div>

      <div className="backup-cards-grid">
        {/* Export Card */}
        <div className="settings-card">
          <h3>Secure Data Export</h3>
          <p className="card-hint">
            Download your current database content. Password hashes and active session tokens are strictly excluded.
          </p>

          <div className="export-buttons-stack">
            <a
              href={api.getExportUrl('csv_phrases')}
              download="love.csv"
              className="action-btn action-btn-secondary export-download-btn"
            >
              Export Love Phrases (CSV)
            </a>

            <a
              href={api.getExportUrl('markdown_nicknames')}
              download="she.md"
              className="action-btn action-btn-secondary export-download-btn"
            >
              Export Nicknames (Markdown)
            </a>

            <a
              href={api.getExportUrl('json_settings')}
              download="heart-settings.json"
              className="action-btn action-btn-secondary export-download-btn"
            >
              Export Visual Settings (JSON)
            </a>
          </div>
        </div>

        {/* Import Card */}
        <div className="settings-card">
          <h3>Bulk Import Content</h3>
          <p className="card-hint">
            Import entries into D1 with automatic deduplication against existing records.
          </p>

          {importResult && <div className="notice-banner banner-success">{importResult}</div>}
          {errorMsg && <div className="notice-banner banner-danger">{errorMsg}</div>}

          <form onSubmit={handleImportSubmit} className="import-form">
            <div className="form-field">
              <label htmlFor="import-type">Content Type</label>
              <select
                id="import-type"
                value={importType}
                onChange={e =>
                  setImportType(e.target.value as 'csv_phrases' | 'markdown_nicknames')
                }
                className="select-field"
              >
                <option value="csv_phrases">Multilingual Love Phrases (CSV)</option>
                <option value="markdown_nicknames">Affectionate Nicknames (Markdown List)</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="file-upload">Choose File (.csv or .md)</label>
              <input
                id="file-upload"
                type="file"
                accept=".csv,.md,.txt"
                onChange={handleFileUpload}
                className="file-input"
              />
            </div>

            <div className="form-field">
              <label htmlFor="raw-content">Or Paste Raw Content</label>
              <textarea
                id="raw-content"
                rows={6}
                value={rawContent}
                onChange={e => setRawContent(e.target.value)}
                placeholder={
                  importType === 'csv_phrases'
                    ? 'phrase,language\nI love you,English\nআমি তোমায় ভালোবাসি,Bengali'
                    : '# Her names\n\n- rasmalai\n- peekaboo'
                }
                className="textarea-field"
              />
            </div>

            <button
              type="submit"
              disabled={isImporting || !rawContent.trim()}
              className="action-btn action-btn-primary"
            >
              {isImporting ? 'Processing Import...' : 'Run Validated Import'}
            </button>
          </form>
        </div>

        {/* Database Health & Maintenance Card */}
        <div className="settings-card" style={{ gridColumn: '1 / -1' }}>
          <h3>Database Optimization & Maintenance</h3>
          <p className="card-hint">
            The database continuously purges expired sessions, flushes rate-limiting records older than 1 hour, and prunes audit logs beyond 500 entries in the background.
          </p>

          {optimizeResult && (
            <div className="notice-banner banner-success" style={{ margin: 'var(--space-sm) 0' }}>
              {optimizeResult}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-md)', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Automatic Maintenance: <strong style={{ color: 'var(--red-6)' }}>ACTIVE</strong> (Self-cleaning on auth & logins)
            </span>
            <button
              type="button"
              disabled={isOptimizing}
              className="action-btn action-btn-secondary"
              onClick={handleOptimize}
            >
              {isOptimizing ? 'Optimizing...' : 'Run Database Maintenance Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
